import React, { useEffect, useState } from 'react';
import {
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Box,
  Button,
  Chip,
  Popover,
  MenuItem,
  Select,
  CircularProgress,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { ClearIcon } from '@mui/x-date-pickers';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Root, classes } from './utils';
import { lightPalette } from '../../../theme';
import { globalClasses } from '../utils/globalUtils';
import {
  deleteNotification,
  fetchNotifications,
  markAllAsReadNotifications,
  markNotificationAsRead,
} from '../../../services/tabSection';
import { CustomPurpleBadge, CustomRedBadge } from '../../layout/navbar/utils';
import { completeNotification } from '../../../store/socketSlice';
import CustomTooltip from '../utils/CustomTooltip';

const Notification = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState('');
  const [notificationList, setNotificationList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationStatus, setNotificationStatus] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(7);
  const [loading, setLoading] = useState(false);
  const [remainingCount, setRemainingCount] = useState(0);
  const [isEnter, setIsEnter] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const calculateTime = (UtcTime) => {
    const localTime = moment.utc(UtcTime).local();

    const formattedNotificationDate = localTime.format('DD-MM-YYYY');
    const formattedNotificationTime = localTime.format('hh:mm A');

    return `${formattedNotificationDate}  ${formattedNotificationTime}`;
  };

  const fetchData = async (
    invoiceName,
    status,
    invoiceStatus,
    notificationId,
  ) => {
    setLoading(true);

    const data = {
      searchData: invoiceName || '',
      notificationStatus: status || '',
      uploadStatus: invoiceStatus || '',
    };
    const notifications = await fetchNotifications(data, page, limit);
    setRemainingCount(notifications?.remainingCount);
    setUnreadCount(notifications?.unreadCount);
    if (notifications && notifications?.notifications?.length > 0) {
      if (page === 1) {
        setNotificationList(notifications?.notifications);
      } else {
        setNotificationList((prevNotifications) => [
          ...prevNotifications.filter(
            (notification) => notification.id !== notificationId,
          ),
          ...notifications.notifications,
        ]);
      }
    }
    setLoading(false);
  };

  const handleMarkAsAllReadButton = async () => {
    const responseNotifyAllRead = await markAllAsReadNotifications();
    if (responseNotifyAllRead?.status === 200) {
      setNotificationList((prevNotifications) =>
        prevNotifications.map((item) => ({ ...item, status: 'read' })),
      );
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    fetchData(searchValue, notificationStatus, uploadStatus);
  }, [page]);

  useEffect(() => {
    setPage(1); // Reset page when search or filter changes
  }, [searchValue, notificationStatus, uploadStatus]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  // textfield text handler when user type in it
  const handleChange = async (event) => {
    const InvoiceName = event.target.value;
    setSearchValue(InvoiceName);
    if (InvoiceName === '' && isEnter) {
      setIsEnter(false);
      await fetchData('', notificationStatus, uploadStatus);
    }
  };

  // clear search textfield
  const handleClearSearch = async () => {
    setSearchValue('');
    setIsEnter(false);
    await fetchData('', notificationStatus, uploadStatus);
  };

  // handle readNotification status
  const handleReadNotification = async (notificationId) => {
    const readNotification = await markNotificationAsRead(notificationId);
    dispatch(completeNotification(readNotification?.data?.notification));
    await fetchData(searchValue, notificationStatus, uploadStatus);
  };

  // open popup window for filter
  const filterNotifications = (e) => {
    setAnchorEl(e.currentTarget);
  };

  // filter notification according to selection
  const handleFilterData = async () => {
    setAnchorEl(null);
    setPage(1);
    if (
      notificationStatus.trim().length > 0 ||
      uploadStatus.trim().length > 0
    ) {
      await fetchData(searchValue, notificationStatus, uploadStatus);
    }
  };

  // clear filter for showcase notifications
  const handleClearData = async () => {
    setNotificationStatus('');
    setUploadStatus('');
    setAnchorEl(null);
    if (notificationStatus.trim().length > 0) {
      await fetchData(searchValue, '', '');
    }
  };

  // handle searchData on enter
  const handleSearchData = async (e) => {
    if (e.key === 'Enter') {
      const data = e.target.value.trim();
      if (data.length > 0) {
        setIsEnter(true);
        await fetchData(e.target.value, notificationStatus, uploadStatus);
      }
    }
  };

  // change notification status for filter
  const handleNotificationStatusChange = (event) => {
    setNotificationStatus(event.target.value);
  };

  // change invoice status for filter
  const handleInvoiceStatusChange = (event) => {
    setUploadStatus(event.target.value);
  };

  const handleScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.clientHeight + 1 >= target.scrollHeight &&
      remainingCount > 0
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleDeleteNotification = async (notificationId) => {
    const response = await deleteNotification(notificationId);
    if (response?.status === 200) {
      await fetchData(
        searchValue,
        notificationStatus,
        uploadStatus,
        notificationId,
      );
    }
  };

  const documentTypeNameFunc = (notificationDocType) => {
    switch (notificationDocType) {
      case 'BS':
        return 'Bank Statement';
      case 'CC':
        return 'Credit Card Statement';
      case 'sales':
        return 'Sales';
      case 'invoice':
        return 'Direct Expenses';
      case 'receipt':
        return 'Indirect Expenses';
      default:
        return 'all-documents';
    }
  };

  const getDocumentTypeNav = (value) => {
    switch (value) {
      case 'BS':
        return 'bank-statement';
      case 'CC':
        return 'creditcardstatement';
      case 'sales':
        return 'sale';
      case 'invoice':
        return 'invoice';
      case 'receipt':
        return 'receipt';
      default:
        return '';
    }
  };

  const getNotificationLink = (notificationData) => {
    if (notificationData?.document) {
      if (
        notificationData?.title === 'Document Status' ||
        notificationData?.title === 'Document Change'
      ) {
        if (notificationData?.document?.status === 'Completed') {
          return `/${getDocumentTypeNav(notificationData?.document?.document_type)}/${notificationData?.invoice_id}`;
        }
      }
    } else if (
      notificationData?.flow &&
      notificationData?.title === 'Flow Change'
    ) {
      return `/flow-configuration/editor?flowId=${notificationData?.flow_id}`;
    } else if (
      notificationData?.OrganizationSubscription &&
      notificationData?.title === 'Plan Expiry'
    ) {
      return '/account-info?tab=billingInfo';
    } else if (
      notificationData?.payment &&
      notificationData?.title === 'Credits'
    ) {
      return '/account-info?tab=myAccount';
    }

    // Default case
    return '#';
  };

  return (
    <Root className={classes.root}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h4" className={classes.notificationHeading}>
          All Notifications
        </Typography>
        <Button
          variant="contained"
          className={classes.readAllNotificationsBtn}
          startIcon={<DoneAllIcon />}
          onClick={handleMarkAsAllReadButton}
          disabled={unreadCount === 0}
        >
          Mark all as read
        </Button>
      </Box>
      <Box className={classes.notificationHeadingDivider}>
        <Divider />
      </Box>
      <Box className={classes.mainNotificationContent}>
        <Box className={classes.notificationAppBar}>
          <TextField
            size="small"
            placeholder="Search..."
            autoComplete="off"
            onChange={handleChange}
            onKeyDown={handleSearchData}
            className={classes.SearchField}
            value={searchValue}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    className={`${
                      searchValue !== ''
                        ? globalClasses.visibility
                        : globalClasses.notVisibility
                    }
                    `}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            aria-describedby={id}
            className={classes.moreNotificationsFilterBtn}
            onClick={(e) => filterNotifications(e)}
          >
            <FilterAltOutlinedIcon />
            <Typography variant="h5">Filter</Typography>
          </Button>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Root>
              <Box>
                <Box className={classes.notificationFilterWindowHeader}>
                  <Typography>Filter</Typography>
                  <CloseIcon
                    className={classes.filterCloseIcon}
                    onClick={handleClose}
                  />
                </Box>
                <Box className={classes.notificationFilterWindowBody}>
                  <Box className={classes.notificationFilterPopupSelectBox}>
                    <Select
                      size="small"
                      className={classes.notificationFilterPopupSelect}
                      value={notificationStatus}
                      onChange={handleNotificationStatusChange}
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) {
                          return (
                            <Typography variant="body1">
                              Notification
                            </Typography>
                          );
                        }

                        return <Typography>{selected}</Typography>;
                      }}
                      displayEmpty
                    >
                      <MenuItem value="read">Read</MenuItem>
                      <MenuItem value="unread">Unread</MenuItem>
                    </Select>

                    <Select
                      size="small"
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) {
                          return (
                            <Typography variant="body1">Invoice</Typography>
                          );
                        }

                        return <Typography>{selected}</Typography>;
                      }}
                      className={classes.notificationFilterPopupSelectStatus}
                      value={uploadStatus}
                      onChange={handleInvoiceStatusChange}
                      displayEmpty
                    >
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Failed">Failed</MenuItem>
                    </Select>
                  </Box>
                  <Box className={classes.notificationFilterWindowButton}>
                    <Button
                      variant="contained"
                      className={classes.notificationFilterWindowButtonApply}
                      onClick={handleFilterData}
                    >
                      Apply
                    </Button>
                    <Button
                      className={classes.notificationFilterWindowButtonCancel}
                      onClick={handleClearData}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Root>
          </Popover>
        </Box>
        {loading ? (
          <Box className={classes.loaderMainBox}>
            <CircularProgress className={classes.loaderBox} />
          </Box>
        ) : notificationList?.length === 0 ? (
          <Box className={classes.noRecordFoundBox}>
            <Typography variant="h5">No notifications found</Typography>
          </Box>
        ) : (
          <Box className={classes.notificationsList} onScroll={handleScroll}>
            {notificationList?.map((notification) => {
              const importType =
                notification?.document?.document_summaries[0]?.import_type;
              const importOption =
                notification?.document?.document_summaries[0]?.import_option;
              const flowName =
                notification?.document?.document_summaries[0]?.flow_builder
                  ?.name || 'Unknown';
              const folder =
                notification?.document?.document_summaries[0]?.location ||
                'Unknown';
              return (
                <Box
                  className={classes.notificationCard}
                  key={notification?.id}
                  sx={{
                    backgroundColor:
                      notification?.status === 'read'
                        ? lightPalette.paperWhite.darker
                        : lightPalette.pastelLavender.main,
                  }}
                >
                  <Box className={classes.notificationInvoice}>
                    <Box>
                      {notification?.status === 'read' ? (
                        <CustomRedBadge variant="dot" size="small" />
                      ) : (
                        <CustomPurpleBadge variant="dot" size="small" />
                      )}
                    </Box>
                    <Box className={classes.notificationInvoiceBox}>
                      <Box className={classes.notificationInvoiceLink}>
                        <Link
                          to={getNotificationLink(notification)}
                          onClick={() =>
                            handleReadNotification(notification?.id)
                          }
                        >
                          <Typography
                            variant="h5"
                            className={classes.documentRowTypography}
                            color={
                              notification?.status === 'read'
                                ? lightPalette.secondaryBlack.medium
                                : lightPalette.darkPurple.darkest
                            }
                          >
                            {notification?.title}
                          </Typography>
                        </Link>
                        {notification?.document && (
                          <Chip
                            size="small"
                            label={
                              notification?.document?.status === 'Failed'
                                ? 'Failed'
                                : 'Completed'
                            }
                            className={
                              notification?.document?.status === 'Failed'
                                ? `${globalClasses.whiteTextColor} ${classes.notificationChip}`
                                : `${globalClasses.whiteTextColor} ${classes.notificationChip}`
                            }
                            sx={{
                              background:
                                notification?.status === 'read' &&
                                notification?.document?.status === 'Failed'
                                  ? lightPalette.colorRed.medium
                                  : notification?.status === 'unread' &&
                                      notification?.document?.status ===
                                        'Failed'
                                    ? lightPalette.colorRed.main
                                    : notification?.status === 'read' &&
                                        notification?.document?.status ===
                                          'Completed'
                                      ? lightPalette.colorGreen.medium
                                      : notification?.status === 'unread' &&
                                          notification?.document?.status ===
                                            'Completed'
                                        ? lightPalette.colorGreen.main
                                        : '',
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        className={classes.notificationMessage}
                        color={
                          notification?.status === 'read'
                            ? lightPalette.secondaryBlack.medium
                            : lightPalette.secondaryBlack.main
                        }
                        variant="h5"
                      >
                        {notification?.message}
                      </Typography>
                      <Box className={classes.notificationTime}>
                        <AccessTimeIcon
                          className={classes.notificationTimeIcon}
                          sx={{
                            color:
                              notification?.status === 'read'
                                ? lightPalette.secondaryBlack.medium
                                : lightPalette.secondaryBlack.main,
                          }}
                        />
                        <Typography
                          color={
                            notification?.status === 'read'
                              ? lightPalette.secondaryBlack.medium
                              : lightPalette.secondaryBlack.main
                          }
                          variant="body1"
                        >
                          {calculateTime(notification?.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {notification?.document && (
                    <Box py={1} justifyContent="flex-start" width="30%">
                      <Typography
                        variant="body1"
                        color={
                          notification?.status === 'read'
                            ? lightPalette.secondaryBlack.medium
                            : lightPalette.secondaryBlack.main
                        }
                      >
                        Import Option:-{' '}
                        {['OneDrive', 'SharePoint'].includes(importOption)
                          ? `${importOption} - ${folder}`
                          : importOption}
                      </Typography>
                      <Typography
                        variant="body1"
                        color={
                          notification?.status === 'read'
                            ? lightPalette.secondaryBlack.medium
                            : lightPalette.secondaryBlack.main
                        }
                      >
                        Import Type:-{' '}
                        {importType === 'Automated'
                          ? `flow - ${flowName}`
                          : importType}
                      </Typography>
                      <Typography
                        variant="body1"
                        color={
                          notification?.status === 'read'
                            ? lightPalette.secondaryBlack.medium
                            : lightPalette.secondaryBlack.main
                        }
                      >
                        Document Type:-{' '}
                        {documentTypeNameFunc(
                          notification?.document?.document_type,
                        )}
                      </Typography>
                    </Box>
                  )}
                  <Box height="100%" alignItems="flex-start">
                    <CustomTooltip>
                      <CloseIcon
                        className={globalClasses.cursorPointerClass}
                        sx={{
                          color:
                            notification?.status === 'read'
                              ? lightPalette.secondaryBlack.medium
                              : lightPalette.secondaryBlack.main,
                        }}
                        onClick={() =>
                          handleDeleteNotification(notification?.id)
                        }
                      />
                    </CustomTooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Root>
  );
};

export default Notification;
